import React from 'react'
import { Cell, Pie, PieChart } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { formatNumber, formatPercent } from '@/lib/dashboardFormat'

export type DonutSlice = {
  id: string
  label: string
  value: number
  pct: number
}

const defaultPalette = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
]

const chartConfig = {
  value: { label: 'Quantidade' },
} satisfies ChartConfig

interface DistributionDonutProps {
  title: string
  description?: string
  slices: DonutSlice[]
  centerLabel: string
  emptyMessage: string
  loading?: boolean
  palette?: string[]
  maxSlices?: number
}

const DistributionDonut: React.FC<DistributionDonutProps> = ({
  title,
  description,
  slices,
  centerLabel,
  emptyMessage,
  loading = false,
  palette = defaultPalette,
  maxSlices = 6,
}) => {
  // Everything beyond the top slices collapses into one bucket, otherwise a long tail
  // of one-off reasons turns the donut into confetti
  const visible = slices.slice(0, maxSlices)
  const rest = slices.slice(maxSlices)
  const chartSlices =
    rest.length > 0
      ? [
          ...visible,
          {
            id: '__others__',
            label: `Outros (${rest.length})`,
            value: rest.reduce((sum, slice) => sum + slice.value, 0),
            pct: rest.reduce((sum, slice) => sum + slice.pct, 0),
          },
        ]
      : visible

  const total = slices.reduce((sum, slice) => sum + slice.value, 0)

  if (loading) {
    return (
      <div className="rounded-[20px] border border-border bg-card p-5 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-4 h-5 w-1/2 rounded bg-card-muted" />
          <div className="mx-auto h-[180px] w-[180px] rounded-full bg-card-muted" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 rounded-[20px] border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-col gap-1">
        <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
        {description && (
          <p className="text-[12px] leading-snug text-muted-foreground">{description}</p>
        )}
      </div>

      {chartSlices.length === 0 || total === 0 ? (
        <div className="flex flex-1 items-center justify-center py-10">
          <p className="max-w-[240px] text-center text-[13px] text-muted-foreground">
            {emptyMessage}
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
          <div className="relative shrink-0">
            <ChartContainer className="h-[172px] w-[172px] aspect-square" config={chartConfig}>
              <PieChart>
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      hideIndicator
                      labelFormatter={(_, payload) =>
                        (payload?.[0]?.payload as { label?: string })?.label ?? ''
                      }
                      formatter={(value, _name, item) => {
                        const pct = (item?.payload as { pct?: number })?.pct ?? 0
                        return (
                          <span className="flex flex-col">
                            <span className="font-semibold text-foreground">
                              {formatNumber(Number(value))}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {formatPercent(pct)} do total
                            </span>
                          </span>
                        )
                      }}
                    />
                  }
                />
                <Pie
                  data={chartSlices}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={54}
                  outerRadius={82}
                  paddingAngle={2}
                  strokeWidth={0}
                >
                  {chartSlices.map((slice, index) => (
                    <Cell key={slice.id} fill={palette[index % palette.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-[22px] font-bold leading-none text-foreground">
                {formatNumber(total)}
              </span>
              <span className="mt-1 text-[11px] text-muted-foreground">{centerLabel}</span>
            </div>
          </div>

          <ul className="flex w-full flex-col gap-2">
            {chartSlices.map((slice, index) => (
              <li key={slice.id} className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: palette[index % palette.length] }}
                  />
                  <span className="truncate text-[13px] text-foreground" title={slice.label}>
                    {slice.label}
                  </span>
                </span>
                <span className="flex shrink-0 items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-foreground">
                    {formatNumber(slice.value)}
                  </span>
                  <span className="w-11 text-right text-[11px] text-muted-foreground">
                    {formatPercent(slice.pct)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default DistributionDonut
